<template>
  <div class="contract-container">
    <div class="pdf-viewer-container">
      <div class="pdf-toolbar">
        <div class="toolbar-left">
          <van-button @click="goBack">
            返回
          </van-button>

        </div>
        <div class="toolbar-center">
          <span class="pdf-title">{{ getCurrentPdfName() }}</span>
        </div>
        <div class="toolbar-right">
          <van-button @click="previousPage" :disabled="currentPage <= 1">
           上一页
          </van-button>
          <van-button @click="nextPage" :disabled="currentPage >= totalPages">
          下一页
          </van-button>
          <!-- 最后一页显示签署按钮 -->
          <van-button type="primary" @click="onSignButtonClick">
            签署
          </van-button>
        </div>
      </div>

      <!-- 多PDF时显示标签页 -->
      <div v-if="pdfList.length > 1" class="pdf-tabs-container">
        <van-tabs v-model="currentPdfIndex" @change="onTabChange" sticky>
          <van-tab
            v-for="(pdf, index) in pdfList"
            :key="pdf.url"
            :title="`合同${index + 1}`"
          />
        </van-tabs>
      </div>

      <div class="pdf-content">
        <div v-if="loading" class="loading-container">
          <van-loading type="spinner" color="#1989fa" size="24px">
            正在加载PDF文件...
          </van-loading>
        </div>
        <div v-if="error" class="error-container">
          <p>{{ error }}</p>
          <van-button type="default" @click="retryLoad">重新加载</van-button>
        </div>
        <div v-if="currentPdfSrc && !error" class="pdf-viewer" :class="{ 'pdf-loading': loading }">
          <pdf
            :src="currentPdfSrc"
            :page="Math.max(1, currentPage)"
            @num-pages="onNumPages"
            @loaded="onPdfLoaded"
            @error="onPdfError"
            @page-loaded="onPageLoaded"
            @link-clicked="onLinkClicked"
            class="pdf-page"
          />

           <!-- 在最后一页显示确认按钮 -->
          <div v-if="currentPage === totalPages && !showSignature" class="confirm-section">
            <div class="confirm-content">
              <h3>合同确认</h3>
              <p>请仔细阅读合同内容，确认无误后点击下方按钮进行签名确认。</p>
              <van-button type="primary" @click="showConfirmDialog" class="custom-confirm-button">
                开始人脸核身
              </van-button>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- 确认对话框 -->
    <van-dialog
      v-model="showConfirmModal"
      title="合同确认"
      message="请先进行人脸核身，成功后再完成签名。"
      show-cancel-button
      cancel-button-text="取消"
      confirm-button-text="开始人脸核身"
      cancel-button-color="#969799"
      confirm-button-color="#1989fa"
      @confirm="callFxqFaceAPI"
      @cancel="closeConfirmDialog"
      class-name="custom-dialog"
    />

    <!-- 签名弹窗 -->
    <van-dialog
      v-model="showSignatureDialog"
      title="合同签名"
      :show-confirm-button="false"
      :show-cancel-button="false"
      :close-on-click-overlay="false"
      class="signature-dialog"
    >
      <SignatureDialog
        @confirm="onSignatureConfirm"
        @cancel="onSignatureCancel"
      />
    </van-dialog>
  </div>
</template>

<script>
import pdf from 'vue-pdf'
import SignatureDialog from '@/components/SignatureDialog.vue'
import requestHTAPI from '@/axios/HantangTax'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'Contract',
  components: {
    pdf,
    SignatureDialog
  },
  data () {
    return {
      contractData: null, // 合同数据
      pdfList: [], // PDF文件列表
      currentPdfIndex: 0, // 当前PDF索引
      currentPdfSrc: null, // 当前PDF加载任务
      loading: false,
      error: null,
      currentPage: 1,
      totalPages: 0,
      showSignature: false,
      showConfirmModal: false,
      showSignatureDialog: false,
      signatureData: null,
      pdfLoadingTasks: new Map() // PDF加载任务缓存
    }
  },
  mounted () {
    this.loadContractInfo()

    // 检查是否有保存的人脸识别会话数据
    this.checkFaceSessionData()
  },
  computed: {
    ...mapGetters([
      'getContractData',
      'getFaceSessionData'
    ])
  },
  watch: {
    currentPdfIndex (newIndex, oldIndex) {
      if (newIndex !== oldIndex && oldIndex !== undefined) {
        this.loadPdf(newIndex)
      }
    }
  },

  methods: {
    ...mapActions([
      'saveContractData',
      'saveSignatureData',
      'saveFaceSessionData',
      'clearAllContractData'
    ]),
    loadContractInfo () {
      try {
        this.resetState()

        // 从Vuex获取合同数据
        const contractData = this.getContractData

        if (contractData) {
          this.contractData = contractData

          // 初始化PDF列表
          if (this.contractData.dataList && this.contractData.dataList.length > 0) {
            this.pdfList = this.contractData.dataList
              .map((item, index) => ({
                url: item.path,
                name: item.levyName || `合同文件 ${index + 1}`,
                contractNo: item.contractNo,
                levyId: item.levyId
              }))
              .filter(pdf => pdf.url && pdf.url.trim() !== '')

            if (this.pdfList.length > 0) {
              this.loadPdf(0)
            } else {
              throw new Error('PDF列表为空或所有PDF文件无效')
            }
          } else {
            // 使用默认PDF
            const defaultUrl = this.$route.query.pdfUrl
            if (defaultUrl && defaultUrl.trim() !== '') {
              this.pdfList = [{ url: defaultUrl, name: '合同文件' }]
              this.loadPdf(0)
            } else {
              throw new Error('默认PDF URL无效')
            }
          }
        } else {
          // 从路由参数获取PDF URL
          const routeUrl = this.$route.query.pdfUrl
          if (routeUrl && routeUrl.trim() !== '') {
            this.pdfList = [{ url: routeUrl, name: '合同文件' }]
            this.loadPdf(0)
          } else {
            throw new Error('路由PDF URL无效')
          }
        }
      } catch (error) {
        console.error('加载合同信息失败:', error)
        this.error = error.message || '加载合同信息失败，请重试'
        this.loading = false
        this.$toast && this.$toast(this.error)
      }
    },
    // 重置状态
    resetState () {
      this.currentPage = 1
      this.totalPages = 0
      this.loading = false
      this.error = null
      this.currentPdfSrc = null
    },
    // 加载PDF
    loadPdf (index) {
      if (!this.pdfList || index < 0 || index >= this.pdfList.length) {
        this.error = 'PDF索引无效'
        return
      }

      const pdfInfo = this.pdfList[index]
      if (!pdfInfo || !pdfInfo.url) {
        this.error = 'PDF文件信息无效'
        return
      }

      this.currentPdfIndex = index
      this.loading = true
      this.error = null
      this.currentPage = 1
      this.totalPages = 0

      // 检查缓存
      if (this.pdfLoadingTasks.has(pdfInfo.url)) {
        this.currentPdfSrc = this.pdfLoadingTasks.get(pdfInfo.url)
      } else {
        // 创建新的加载任务
        const loadingTask = pdf.createLoadingTask(pdfInfo.url)
        this.pdfLoadingTasks.set(pdfInfo.url, loadingTask)
        this.currentPdfSrc = loadingTask
      }
    },
    onPdfLoaded () {
      this.loading = false
      this.error = null

      // 多PDF模式下显示切换成功提示
      if (this.pdfList && this.pdfList.length > 1) {
        const currentPdf = this.pdfList[this.currentPdfIndex]
        const pdfName = currentPdf ? currentPdf.name : `PDF ${this.currentPdfIndex + 1}`
        this.$toast && this.$toast(`已切换到: ${pdfName}`)
      }
    },
    onNumPages (numPages) {
      this.totalPages = numPages || 1
    },
    onPdfError (error) {
      console.error('PDF加载失败:', error)
      this.loading = false

      // 根据错误类型设置不同的错误信息
      let errorMessage = 'PDF文件加载失败，请检查网络连接或文件是否存在'
      if (error && error.message) {
        if (error.message.includes('fetch')) {
          errorMessage = 'PDF文件下载失败，请检查网络连接'
        } else if (error.message.includes('Invalid PDF')) {
          errorMessage = 'PDF文件格式无效或已损坏'
        } else if (error.message.includes('password')) {
          errorMessage = 'PDF文件需要密码，暂不支持加密文件'
        }
      }

      this.error = errorMessage
      this.$toast && this.$toast('PDF加载失败，请重试')
    },

    previousPage () {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },
    nextPage () {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },
    showConfirmDialog () {
      this.showConfirmModal = true
    },
    closeConfirmDialog () {
      this.showConfirmModal = false
    },
    onTabChange (index) {
      // 标签页切换由 watch 监听器处理
    },
    getCurrentPdfName () {
      if (this.pdfList.length > 0 && this.currentPdfIndex < this.pdfList.length) {
        return this.pdfList[this.currentPdfIndex].name
      }
      return '合同文件'
    },
    retryLoad () {
      this.$toast && this.$toast('正在重新加载...')
      this.loadPdf(this.currentPdfIndex)
    },
    startSignature () {
      this.showConfirmModal = false
      this.openSignatureDialog()
    },
    openSignatureDialog () {
      // 进入签名时隐藏确认区域，避免误触再次核身
      this.showConfirmModal = false
      this.showSignature = true
      this.showSignatureDialog = true
    },
    onSignatureConfirm (signatureData) {
      this.signatureData = signatureData
      this.saveSignatureData(signatureData)
      this.showSignatureDialog = false

      this.$toast('签名成功！')
      // 签名成功后完成合同签署
      this.callFxqTwoStepAPI()
    },
    onSignatureCancel () {
      this.showSignatureDialog = false
      console.log('用户取消签名')
    },

    // 调用人脸识别接口（先核身，后签名）
    callFxqFaceAPI () {
      try {
        // 从合同数据中获取参数
        const params = {
          id: this.contractData?.id || '',
          merId: this.contractData?.merId || ''
        }

        console.log('调用fxqFace接口，参数:', params)

        requestHTAPI.fxqFace(params).then(res => {
          console.log('fxqFace接口响应:', res)
          if (res.data.code === '0000') {
            this.$toast && this.$toast('正在跳转到人脸识别页面...')

            // 从data字段中获取URL并跳转
            const faceUrl = res.data.data
            if (faceUrl) {
              // 保存当前会话数据到Vuex（暂未签名，仅记录合同与位置）
              const sessionData = {
                contractData: this.contractData,
                currentPage: this.currentPage,
                currentPdfIndex: this.currentPdfIndex,
                faceVerified: false,
                faceVerificationStarted: true
              }
              this.saveContractData(this.contractData)
              this.saveFaceSessionData(sessionData)

              // 监听页面可见性变化，检测用户是否从人脸识别页面返回
              // 返回后不自动弹出签名框，改为用户点击“签署”后进入签名

              // 跳转到人脸识别页面
              window.location.href = faceUrl
            } else {
              this.$toast && this.$toast('未获取到人脸识别链接')
            }
          } else {
            this.$toast && this.$toast(res.data.message || '人脸识别调用失败')
          }
        }).catch(error => {
          console.error('fxqFace接口调用失败:', error)
          this.$toast && this.$toast('人脸识别调用失败，请重试')
        })
      } catch (error) {
        console.error('调用fxqFace接口时发生错误:', error)
        this.$toast && this.$toast('调用人脸识别接口时发生错误')
      }
    },
    // 检查人脸识别会话数据（核身成功后不自动弹签名）
    checkFaceSessionData () {
      try {
        // 优先取Vuex，其次从localStorage持久化中兜底
        let sessionData = this.getFaceSessionData
        if (!sessionData) {
          try {
            const persisted = localStorage.getItem('vuex-contract-state')
            if (persisted) {
              const parsed = JSON.parse(persisted)
              sessionData = parsed.faceSessionData || null
            }
          } catch (e) {
            console.warn('读取本地人脸会话数据失败', e)
          }
        }

        if (sessionData && sessionData.faceVerified) {
          console.log('已完成人脸核身，可进行签署')
          // 不自动弹出签名框，等待用户点击“签署”
          return
        }

        // 若已发起过核身但未成功，避免自动再次触发，等待用户点击
        if (sessionData && sessionData.faceVerificationStarted) {
          console.log('已发起过人脸核身，等待用户确认是否重试')
          return
        }

        // 未核身且未发起过，则自动触发人脸核身（首次进入）
        {
          // 未核身则自动触发人脸核身
          if (!this.contractData || !this.contractData.id || !this.contractData.merId) {
            console.warn('合同数据缺失，无法发起人脸核身')
            this.$toast && this.$toast('合同数据缺失，无法发起人脸核身')
            return
          }
          this.$toast && this.$toast('即将进行人脸核身...')
          // 短暂延迟，确保页面和合同数据已就绪
          setTimeout(() => {
            this.callFxqFaceAPI()
          }, 300)
        }
      } catch (error) {
        console.error('检查人脸识别会话数据时发生错误:', error)
      }
    },
    // 完成签署跳转到等待页面
    callFxqTwoStepAPI () {
      // 改为跳转到签署等待页面完成后续逻辑
      this.$router.push('/mine/signing')
    },
    // “签署”按钮点击逻辑：已核身则进入签名，未核身则提示并发起核身
    onSignButtonClick () {
      try {
        const sessionData = this.getFaceSessionData
        if (sessionData && sessionData.faceVerified) {
          // 已核身，进入签名
          this.openSignatureDialog()
          return
        }
        // 未核身，弹出确认对话框，提示并发起人脸核验
        this.showConfirmDialog()
      } catch (error) {
        console.error('处理签署按钮点击时发生错误:', error)
      }
    },
    downloadContract () {
      if (this.pdfUrl) {
        // 创建下载链接
        const link = document.createElement('a')
        link.href = this.pdfUrl
        link.download = 'contract.pdf'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        this.$toast && this.$toast('PDF文件不存在')
      }
    },
    retryLoadPdf () {
      this.loading = true
      this.error = null
      // 重新加载PDF
      this.loadContractInfo()
    },
    goBack () {
      this.$router.go(-1)
    },

    onPageLoaded (page) {
      // 页面加载完成处理
    },
    onLinkClicked (link) {
      // 处理PDF内部链接点击
      if (typeof link === 'number') {
        this.currentPage = link
      }
    }

  }
}
</script>

<style scoped>
.contract-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f5f5f5;
}

.pdf-viewer-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: white;
  margin: 10px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.pdf-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background-color: #fafafa;
  border-bottom: 1px solid #e8e8e8;
}

.toolbar-left,
.toolbar-center,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.zoom-info,
.page-info {
  font-size: 14px;
  color: #666;
  min-width: 60px;
  text-align: center;
}

.pdf-content {
  flex: 1;
  width: 100%;
  height: 100%;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
}

.pdf-viewer {
  width: 100%;
  height: 100%;
  position: relative;
}

.pdf-viewer.pdf-loading {
  opacity: 0.3;
  pointer-events: none;
}

.pdf-page {
  width: 100%;
  height: 100%;
}

/* 确认按钮区域 */
.confirm-section {
  width: 100%;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
}

.confirm-content {
  text-align: center;
}

.confirm-content h3 {
  color: #333;
  margin-bottom: 10px;
  font-size: 18px;
}

.confirm-content p {
  color: #666;
  margin-bottom: 20px;
  line-height: 1.5;
}

.contract-actions {
  padding: 15px 20px;
  background: white;
  border-top: 1px solid #e8e8e8;
  display: flex;
  justify-content: center;
}

/* 签名弹窗样式 */
.signature-dialog {
  width: 90vw !important;
  max-width: 800px !important;
  height: auto !important;
  max-height: 90vh !important;

  .van-dialog__content {
    padding: 0;
  }
}

/* PDF工具栏样式 */
.pdf-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.toolbar-left, .toolbar-right {
  display: flex;
  gap: 8px;
  align-items: center;
}

.toolbar-center {
  flex: 1;
  text-align: center;
  padding: 0 15px;
}

.pdf-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* PDF标签页样式 */
.pdf-tabs-container {
  background: white;
  border-bottom: 1px solid #e9ecef;
}

.tab-content {
  padding: 15px 20px;
  background: #f8f9fa;
}

.contract-info {
  text-align: center;
}

.contract-name {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin: 0 0 8px 0;
}

.contract-no {
  font-size: 14px;
  color: #666;
  margin: 0;
}

/* 自定义标签页样式 */
.pdf-tabs-container .van-tabs__wrap {
  background: white;
}

.pdf-tabs-container .van-tab {
  font-size: 14px;
  color: #666;
}

.pdf-tabs-container .van-tab--active {
  color: #1989fa;
  font-weight: 500;
}

.pdf-tabs-container .van-tabs__line {
  background-color: #1989fa;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .toolbar-center {
    display: none;
  }

  .pdf-title {
    display: none;
  }

  .pdf-tabs-container .van-tab {
    font-size: 12px;
  }

  .contract-name {
    font-size: 14px;
  }

  .contract-no {
    font-size: 12px;
  }
}
</style>
